import React, { useEffect, useState } from "react";
import { OrderDetails, FullStatus, FullFile, FullMessage } from "../api/ApiTypes.ts";
import {
    User,
    FileIcon,
    MessageCircleIcon,
    Clock,
    AlertTriangle,
    CircleArrowOutUpRight,
    ChevronDown,
    ChevronRight,
    Loader2
} from 'lucide-react';
import { formatDateTime, formatDate, formatCurrency } from "../utils/dateUtils.ts";
import { HtmlContent } from "./HtmlConent.tsx";
import {useOrderApi} from "../context/ApiContext.tsx";

type TimelineItem = {
    id: number;
    date: string;
    type: 'message' | 'file' | 'status';
    title: string;
    details?: string;
    userName?: string;
    icon: React.ReactNode;
    itemData: FullStatus | FullFile | FullMessage;
};

export interface MiniOrderDetailsProps {
    orderId: string;
}

export const MiniOrderDetails: React.FC<MiniOrderDetailsProps> = ({ orderId }) => {
    const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
    const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const orderApi = useOrderApi();
    // Fetch order details
    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!orderId) {
                setError("Order ID is required");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const details = await orderApi.getOrderDetails(orderId);
                setOrderDetails(details);
            } catch (error) {
                console.error("Failed to fetch order details:", error);

                try {
                    const wasInvited = await orderApi.wasInvited(orderId);
                    if (wasInvited) {
                        setError("This order is no longer available");
                    } else {
                        setError("You don't have permission to view this order");
                    }
                } catch (inviteError) {
                    setError("Failed to load order details");
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderId]);

    // Process timeline items when order details change
    useEffect(() => {
        if (orderDetails) {
            const items: TimelineItem[] = [];

            // Add status items
            if (orderDetails.histories && orderDetails.histories.length > 0) {
                orderDetails.histories
                    .filter(status => !status.hide)
                    .forEach(status => {
                        items.push({
                            id: status.id,
                            date: status.statusDate,
                            type: 'status',
                            title: status.orderStatusDescription,
                            details: status.orderStatusMessage,
                            icon: <Clock className="h-4 w-4 text-blue-600" />,
                            itemData: status
                        });
                    });
            }

            // Add file items
            if (orderDetails.files && orderDetails.files.length > 0) {
                orderDetails.files
                    .filter(file => !file.hide)
                    .forEach(file => {
                        const userName = [file.uploadedByFirstName, file.uploadedByLastName]
                            .filter(Boolean)
                            .join(' ') || 'System';

                        items.push({
                            id: file.id,
                            date: file.uploadDateTime,
                            type: 'file',
                            title: file.fileName,
                            details: file.fileTypeDescription,
                            userName: userName,
                            icon: <FileIcon className="h-4 w-4 text-green-600" />,
                            itemData: file
                        });
                    });
            }

            // Add message items
            if (orderDetails.messages && orderDetails.messages.length > 0) {
                orderDetails.messages
                    .filter(message => !message.hide)
                    .forEach(message => {
                        const userName = [message.messageFromFirstName, message.messageFromLastName]
                            .filter(Boolean)
                            .join(' ');

                        items.push({
                            id: message.id,
                            date: message.dateCreated,
                            type: 'message',
                            title: message.messageTypeDescription,
                            details: message.messageText,
                            userName: userName,
                            icon: <MessageCircleIcon className="h-4 w-4 text-purple-600" />,
                            itemData: message
                        });
                    });
            }

            // Sort by date (newest first)
            items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            // Initialize expanded state for items with details
            const initialExpandState: Record<string, boolean> = {};
            items.forEach(item => {
                const itemKey = `${item.type}-${item.id}`;
                initialExpandState[itemKey] = false; // All items collapsed by default
            });

            setExpandedItems(initialExpandState);
            setTimelineItems(items);
        }
    }, [orderDetails]);

    const toggleItemExpand = (itemKey: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemKey]: !prev[itemKey]
        }));
    };

    const openOrderDetails = () => {
        window.open(`/orderdetails/${orderId}`, '_blank');
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden max-h-[80vh] flex items-center justify-center p-8">
                <div className="flex flex-col items-center">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                    <p className="text-sm text-slate-600 mt-2">Loading order details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden max-h-[80vh] flex items-center justify-center p-8">
                <div className="text-center">
                    <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    // No order details state
    if (!orderDetails) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden max-h-[80vh] flex items-center justify-center p-8">
                <p className="text-sm text-slate-600">No order details available</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="bg-slate-50 border-b border-slate-200 p-3">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-base font-semibold text-slate-800">
                            Order #{orderDetails.fileNumber}
                        </h2>
                        <p className="text-xs text-slate-500">
                            {orderDetails.propertyAddress}
                        </p>
                    </div>
                    <button
                        onClick={openOrderDetails}
                        className="text-blue-600 hover:text-blue-800 flex items-center text-xs font-medium"
                    >
                        View Full Details
                        <CircleArrowOutUpRight className="h-3 w-3 ml-1" />
                    </button>
                </div>
            </div>

            {/* Key Info */}
            <div className="p-3 border-b border-slate-200">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                        <span className="text-slate-500">Due Date:</span>
                        <span className="ml-1 font-medium text-slate-800">
                            {formatDate(orderDetails.appraiserDueDate)}
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-500">Fee:</span>
                        <span className="ml-1 font-medium text-slate-800">
                            {formatCurrency(orderDetails.appraiserFee)}
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-500">Property Type:</span>
                        <span className="ml-1 font-medium text-slate-800">
                            {orderDetails.propertyType}
                        </span>
                    </div>
                    <div>
                        <span className="text-slate-500">Loan #:</span>
                        <span className="ml-1 font-medium text-slate-800">
                            {orderDetails.loanNumber || 'N/A'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Alerts (if any) */}
            {orderDetails.alerts && orderDetails.alerts.length > 0 && (
                <div className="px-3 py-2 bg-red-50 border-b border-red-200">
                    <div className="flex items-center">
                        <AlertTriangle className="h-3 w-3 text-red-500 mr-1 flex-shrink-0" />
                        <p className="text-xs text-red-700 font-medium truncate">
                            {orderDetails.alerts.join(', ')}
                        </p>
                    </div>
                </div>
            )}

            {/* Timeline section */}
            <div className="overflow-y-auto flex-grow">
                <div className="p-3">
                    <h3 className="text-xs font-medium text-slate-700 mb-2">Recent Activity</h3>

                    {timelineItems.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">No recent activity</p>
                    ) : (
                        <ul className="space-y-3">
                            {timelineItems.map((item) => {
                                const itemKey = `${item.type}-${item.id}`;
                                const isExpanded = expandedItems[itemKey];
                                const hasDetails = !!item.details;

                                return (
                                    <li key={itemKey} className="border-l-2 border-slate-200 pl-3 pb-3 relative">
                                        <div className="absolute -left-1.5 top-0 w-3 h-3 rounded-full bg-white border-2 border-slate-300"></div>
                                        <div className="flex items-start">
                                            <div className="mt-0.5 mr-2">{item.icon}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex items-center">
                                                        <p className="text-xs font-medium text-slate-700 truncate">
                                                            {item.title}
                                                        </p>
                                                        {hasDetails && (
                                                            <button
                                                                onClick={() => toggleItemExpand(itemKey)}
                                                                className="ml-1 text-slate-500 hover:text-slate-700 focus:outline-none"
                                                                aria-expanded={isExpanded}
                                                                aria-label={isExpanded ? "Collapse details" : "Expand details"}
                                                            >
                                                                {isExpanded ?
                                                                    <ChevronDown className="h-3 w-3" /> :
                                                                    <ChevronRight className="h-3 w-3" />
                                                                }
                                                            </button>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-slate-500 whitespace-nowrap ml-2">
                                                        {formatDateTime(item.date)}
                                                    </span>
                                                </div>
                                                {item.userName && (
                                                    <p className="text-xs text-slate-600 flex items-center mt-0.5">
                                                        <User className="h-2.5 w-2.5 mr-1" />
                                                        {item.userName}
                                                    </p>
                                                )}
                                                {hasDetails && isExpanded && (
                                                    <div className="mt-1 text-xs">
                                                        <div className="bg-slate-50 rounded p-2 text-slate-700">
                                                            <HtmlContent
                                                                content={item.details || ''}
                                                                className="text-xs text-slate-600"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};
